import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter } from "@tanstack/react-router";
import { ArrowRight, Eye, EyeOff, Lock, Mail } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { DemoAccountsPanel } from "@/components/auth/DemoAccountsPanel";
import { SsoButtons } from "@/components/auth/SsoButtons";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { DEMO_PASSWORD } from "@/db/demo-users";
import { signIn } from "@/lib/auth-client";

const loginSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(8, "Password must be at least 8 characters"),
});

type LoginValues = z.infer<typeof loginSchema>;

const inputClasses =
  "w-full rounded-lg border border-input bg-white py-[13px] pr-3.5 pl-10 text-sm text-foreground outline-none transition-[border,box-shadow] duration-[120ms] placeholder:text-muted-foreground focus:border-ring focus:shadow-[0_0_0_3px_color-mix(in_oklab,var(--ring)_18%,transparent)]";

const labelClasses =
  "mb-1.5 block text-[11px] font-semibold tracking-[0.14em] text-muted-foreground uppercase";

export function LoginForm() {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const form = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });

  // Surface OAuth failures: Better Auth redirects back to /login?error=... when
  // a social sign-in is rejected (e.g. a non-@nymbl.app Google or Microsoft
  // account). Toast it and strip the param so it doesn't re-fire on refresh.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has("error")) return;
    toast.error("Sign-in didn't complete. Use your @nymbl.app account, or sign in below.");
    params.delete("error");
    const query = params.toString();
    window.history.replaceState({}, "", `${window.location.pathname}${query ? `?${query}` : ""}`);
  }, []);

  async function signInWith(email: string, password: string) {
    const { error } = await signIn.email({ email, password });
    if (error) {
      toast.error(error.message ?? "Failed to sign in");
      return false;
    }
    return true;
  }

  async function onSubmit(values: LoginValues) {
    const ok = await signInWith(values.email, values.password);
    if (ok) router.navigate({ to: "/dashboard" });
  }

  async function handleDemoSelect(email: string) {
    const ok = await signInWith(email, DEMO_PASSWORD);
    if (ok) router.navigate({ to: "/dashboard" });
  }

  return (
    <div>
      <div className="font-display mb-2.5 text-[10px] font-bold tracking-[0.32em] text-[var(--intensity-8)] uppercase">
        Welcome back
      </div>
      <h2 className="font-display m-0 mb-2 text-[32px] leading-tight font-bold tracking-[-0.01em] text-foreground">
        Sign in
      </h2>
      <p className="mb-6 text-sm leading-[1.5] text-muted-foreground">
        Use your Nymbl workspace account to continue to Staff.
      </p>

      <SsoButtons />

      <div className="my-5 flex items-center gap-3 text-[11px] font-semibold tracking-[0.14em] text-muted-foreground uppercase before:h-px before:flex-1 before:bg-[linear-gradient(90deg,transparent,var(--border),transparent)] before:content-[''] after:h-px after:flex-1 after:bg-[linear-gradient(90deg,transparent,var(--border),transparent)] after:content-['']">
        or with email
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className={labelClasses}>Email</FormLabel>
                <FormControl>
                  <div className="relative flex items-center">
                    <Mail className="pointer-events-none absolute left-3 h-4 w-4 text-muted-foreground" />
                    <input
                      type="email"
                      placeholder="you@workspace.com"
                      autoComplete="email"
                      className={inputClasses}
                      {...field}
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel className={labelClasses}>Password</FormLabel>
                <FormControl>
                  <div className="relative flex items-center">
                    <Lock className="pointer-events-none absolute left-3 h-4 w-4 text-muted-foreground" />
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      autoComplete="current-password"
                      className={inputClasses}
                      {...field}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      className="absolute right-3 flex items-center p-1 text-muted-foreground transition-colors hover:text-[var(--ring)]"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="mt-1 mb-4 flex items-center justify-between text-xs">
            <label className="flex cursor-pointer items-center gap-2 select-none">
              <input type="checkbox" className="h-3.5 w-3.5 accent-[var(--ring)]" defaultChecked />
              <span className="text-muted-foreground">Remember me</span>
            </label>
            <button
              type="button"
              className="cursor-pointer border-0 bg-transparent p-0 font-semibold text-[var(--ring)] hover:underline"
            >
              Forgot password?
            </button>
          </div>

          <button
            type="submit"
            disabled={form.formState.isSubmitting}
            className="font-display group flex w-full items-center justify-center gap-2 rounded-lg border-0 bg-brand-900 px-4 py-3 text-sm font-semibold tracking-[0.02em] text-white transition-[background,transform] duration-150 hover:bg-brand-950 active:translate-y-px disabled:cursor-not-allowed disabled:opacity-70"
          >
            <span>{form.formState.isSubmitting ? "Signing in…" : "Sign in to Nymbl Staff"}</span>
            <ArrowRight className="h-4 w-4 transition-transform duration-150 group-hover:translate-x-px" />
          </button>
        </form>
      </Form>

      <DemoAccountsPanel onSelect={handleDemoSelect} disabled={form.formState.isSubmitting} />
    </div>
  );
}
